import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ExternalLink, Loader2, MessageSquare, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/apiClient';
import { formatList, fmtShortDateTime as formatTime } from '../utils/format';
import { AssistantTranscriptList } from '../features/generate/TranscriptPanel';
import { FilterSearchInput } from '../components/FormComponents';

function sourceChip(source) {
  if (source === 'slack') return 'chip purple';
  if (source === 'cli') return 'chip blue';
  return 'chip gray';
}

function recordLink(entity) {
  if (entity?.entityType === 'screening_session' && entity.entityId) {
    return `/intake/sessions/${entity.entityId}`;
  }
  if (entity?.entityType === 'contract' && entity.entityId) {
    return `/contracts/${entity.entityId}`;
  }
  if (entity?.entityType === 'tprm_request' && entity.entityId) {
    return `/tprm/${entity.entityId}`;
  }
  return null;
}

function recordLabel(entity) {
  const type = String(entity?.entityType || '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
  return `${type || 'Record'}${entity?.entityId ? ` · ${entity.entityId.slice(0, 8)}` : ''}`;
}

function snippet(text) {
  const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'No messages yet';
  return cleaned.length > 140 ? `${cleaned.slice(0, 140)}...` : cleaned;
}

function contractLookupDebug(message) {
  const payload = message?.payload || {};
  return payload.debug?.contractLookup || payload.contractLookup || null;
}

function DebugRow({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <span className="muted" style={{ fontSize: 10, textTransform: 'uppercase' }}>{label}</span>
      <div className="mono" style={{ marginTop: 2, overflowWrap: 'anywhere' }}>{children || '—'}</div>
    </div>
  );
}

function ContractLookupDebug({ debug }) {
  if (!debug) return null;
  const plan = debug.plan || {};
  const fields = debug.search?.fields || {};
  const matches = Array.isArray(debug.matches) ? debug.matches : [];
  return (
    <details
      style={{
        marginTop: 10,
        borderTop: '0.5px solid var(--border-primary)',
        paddingTop: 8,
        color: 'var(--text-secondary)',
      }}
    >
      <summary style={{ cursor: 'pointer', fontSize: 11, userSelect: 'none' }}>
        Lookup debug
        {plan.query ? <span className="mono"> · query: {plan.query}</span> : null}
      </summary>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 8,
          marginTop: 8,
          fontSize: 11,
        }}
      >
        <DebugRow label="Intent">{plan.intent}</DebugRow>
        <DebugRow label="Query">{plan.query || 'none'}</DebugRow>
        <DebugRow label="Type hints">{formatList(plan.typeHints)}</DebugRow>
        <DebugRow label="Source">{[plan.source, plan.model].filter(Boolean).join(' · ')}</DebugRow>
        {plan.days ? <DebugRow label="Days">{plan.days}</DebugRow> : null}
        {plan.confidence ? <DebugRow label="Confidence">{plan.confidence}</DebugRow> : null}
      </div>
      {plan.clarification && (
        <div style={{ marginTop: 8, fontSize: 11 }}>
          <span className="muted" style={{ textTransform: 'uppercase' }}>Clarification</span>
          <div style={{ marginTop: 2 }}>{plan.clarification}</div>
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 11 }}>
        <span className="muted" style={{ textTransform: 'uppercase' }}>Fields</span>
        <div className="mono" style={{ marginTop: 2, overflowWrap: 'anywhere' }}>
          core: {formatList(fields.coreEvidence)} · ranking: {formatList(fields.ranking)} · background: {formatList(fields.backgroundRanking)}
        </div>
      </div>
      {matches.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11 }}>
          <span className="muted" style={{ textTransform: 'uppercase' }}>
            Matches{debug.selectedContractId ? ` · selected ${debug.selectedContractId.slice(0, 8)}` : ''}
          </span>
          <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
            {matches.slice(0, 8).map((match) => (
              <div key={match.id} className="mono" style={{ overflowWrap: 'anywhere' }}>
                {match.score != null ? `${match.score} · ` : ''}
                {match.shortName || match.title || match.counterparty || match.id}
                {match.counterparty ? ` · ${match.counterparty}` : ''}
                {match.counterpartyGroup ? ` · group ${match.counterpartyGroup}` : ''}
                {match.id ? ` · ${match.id.slice(0, 8)}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </details>
  );
}

export default function AdminConversations() {
  const { can, loading: authLoading } = useAuth();
  const canViewConversations = can('view_conversations');
  const canDeleteConversations = can('delete_conversations');
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [source, setSource] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  // Returns a cancel function — a superseded request (filters changed, or a
  // newer manual refresh) must not clobber newer results. Direct callers
  // (refresh button, post-delete) can ignore the return value.
  const loadThreads = useCallback(() => {
    if (!canViewConversations) return () => {};
    let cancelled = false;
    setLoading(true);
    setError('');
    // 'all', not 'active': idle threads auto-expire after a staleness window
    // now, and expired transcripts still matter for admin review.
    const params = new URLSearchParams({
      source,
      status: 'all',
      limit: '100',
    });
    if (query.trim()) params.set('q', query.trim());
    apiRequest(`/admin/assistant-conversations?${params.toString()}`)
      .then((data) => {
        if (cancelled) return;
        const next = data.threads || [];
        setThreads(next);
        setSelectedId((current) => {
          if (current && next.some((thread) => thread.id === current)) return current;
          return next[0]?.id || null;
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [canViewConversations, query, source]);

  useEffect(() => {
    let cancel = () => {};
    const timer = setTimeout(() => { cancel = loadThreads(); }, 200);
    return () => {
      clearTimeout(timer);
      cancel();
    };
  }, [loadThreads]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      setMessages([]);
      return;
    }
    // Same guard for the transcript: clicking thread A then B quickly must
    // not show A's (slower) transcript under B's header.
    let cancelled = false;
    setLoadingDetail(true);
    apiRequest(`/admin/assistant-conversations/${selectedId}?limit=250`)
      .then((data) => {
        if (cancelled) return;
        setSelected(data.thread || null);
        setMessages(data.messages || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setSelected(null);
        setMessages([]);
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => { cancelled = true; };
  }, [selectedId]);

  const selectedLinks = useMemo(() => selected?.linkedEntities || [], [selected]);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteThread = async () => {
    if (!selectedId || deleting) return;
    if (!window.confirm('Delete this conversation and its full transcript? This cannot be undone.')) return;
    setDeleting(true);
    setError('');
    try {
      await apiRequest(`/admin/assistant-conversations/${selectedId}`, { method: 'DELETE' });
      setSelectedId(null);
      setSelected(null);
      setMessages([]);
      loadThreads();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="canvas-inner" style={{ textAlign: 'center', padding: 80 }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
      </div>
    );
  }

  if (!canViewConversations) return <Navigate to="/" replace />;

  const SOURCE_OPTIONS = [
    { value: 'all', label: 'All sources' },
    { value: 'slack', label: 'Slack' },
    { value: 'cli', label: 'CLI' },
    { value: 'web', label: 'Web' },
  ];
  const labelFor = (opts, value) => opts.find((o) => o.value === value)?.label || value;
  const clearAllFilters = () => { setQuery(''); setSource('all'); };
  const activeFilters = [
    query.trim() && { key: 'search', label: `Search: ${query}`, onRemove: () => setQuery('') },
    source !== 'all' && { key: 'source', label: `Source: ${labelFor(SOURCE_OPTIONS, source)}`, onRemove: () => setSource('all') },
  ].filter(Boolean);

  return (
    <div className="canvas-inner animate-fade-in admin-conversations-page">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Admin</div>
          <h1 className="page-title">Assistant conversations</h1>
          <div className="page-subtitle">
            Review Docket bot conversations across Slack, CLI, and future assistant surfaces.
          </div>
        </div>
        <button type="button" className="btn outline sm" onClick={loadThreads} disabled={loading}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      <div style={{ marginBottom: 10 }}>
        <FilterSearchInput
          className="filter-search-wide"
          icon={Search}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people, source, entity id, or transcript text..."
        />
      </div>

      <div className="filter-row natural" style={{ marginBottom: activeFilters.length ? 10 : 18 }}>
        <select className="filter-select" value={source} onChange={(e) => setSource(e.target.value)}>
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {activeFilters.length > 0 && (
        <div className="active-filters" style={{ marginBottom: 18 }}>
          {activeFilters.map((f) => (
            <span key={f.key} className="active-filter-chip">
              {f.label}
              <button type="button" onClick={f.onRemove} aria-label={`Remove ${f.label}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button className="btn ghost xs" onClick={clearAllFilters}>
            <X className="w-3 h-3" />
            Clear all
          </button>
        </div>
      )}

      {error && (
        <div className="callout red" style={{ marginBottom: 16 }}>
          <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-red-500)', flexShrink: 0, marginTop: 1 }} />
          <div className="callout-body" style={{ fontSize: 12.5 }}>{error}</div>
        </div>
      )}

      <div
        className="admin-conversations-layout"
      >
        <div className="card" style={{ overflow: 'hidden' }}>
          <div className="card-header compact">
            <div className="row" style={{ gap: 8 }}>
              <MessageSquare className="w-4 h-4" style={{ color: 'var(--icon-secondary)' }} />
              <span className="card-title">Conversations</span>
            </div>
            <span className="muted mono" style={{ fontSize: 11 }}>{threads.length}</span>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
            </div>
          ) : threads.length === 0 ? (
            <div className="muted" style={{ padding: 28, textAlign: 'center', fontSize: 12.5 }}>
              No conversations match these filters.
            </div>
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 310px)', overflow: 'auto' }}>
              {threads.map((thread) => {
                const active = thread.id === selectedId;
                const title = thread.user?.name || thread.user?.email || thread.slackUserId || 'Unknown user';
                return (
                  <button
                    key={thread.id}
                    type="button"
                    onClick={() => setSelectedId(thread.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: 0,
                      borderBottom: '0.5px solid var(--border-primary)',
                      background: active ? 'var(--surface-alpha-primary)' : 'transparent',
                      padding: '12px 14px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div className="row" style={{ gap: 8, marginBottom: 6 }}>
                      <span className={sourceChip(thread.source)}>{thread.source}</span>
                      <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {title}
                      </span>
                    </div>
                    <div className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 6 }}>
                      {snippet(thread.lastMessageText)}
                    </div>
                    <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
                      <span className="muted mono" style={{ fontSize: 10.5 }}>
                        {formatTime(thread.lastMessageAt || thread.updatedAt)}
                      </span>
                      <span className="muted mono" style={{ fontSize: 10.5 }}>
                        {thread.messageCount} msg{thread.messageCount === 1 ? '' : 's'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="card admin-conversation-detail" style={{ minHeight: 360 }}>
          <div className="card-header compact">
            <div className="row" style={{ gap: 8, minWidth: 0 }}>
              <MessageSquare className="w-4 h-4" style={{ color: 'var(--icon-secondary)' }} />
              <span className="card-title">
                {selected ? (selected.user?.name || selected.user?.email || selected.slackUserId || 'Conversation') : 'Conversation'}
              </span>
            </div>
            <div className="row" style={{ gap: 6, flexShrink: 0 }}>
              {selectedLinks.length === 1 && recordLink(selectedLinks[0]) && (
                <Link to={recordLink(selectedLinks[0])} className="btn ghost xs">
                  <ExternalLink className="w-3 h-3" />
                  Open linked record
                </Link>
              )}
              {selectedId && canDeleteConversations && (
                <button
                  onClick={handleDeleteThread}
                  disabled={deleting}
                  className="btn ghost xs"
                  style={{ color: 'var(--color-red-500)' }}
                  title="Delete this conversation"
                >
                  {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Delete
                </button>
              )}
            </div>
          </div>

          {!selectedId ? (
            <div className="muted" style={{ padding: 44, textAlign: 'center', fontSize: 13 }}>
              Select a conversation to review the transcript.
            </div>
          ) : loadingDetail ? (
            <div style={{ padding: 44, textAlign: 'center' }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
            </div>
          ) : (
            <div className="card-body">
              {selected && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: 10,
                    marginBottom: 18,
                    fontSize: 12,
                  }}
                >
                  <div>
                    <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', marginBottom: 3 }}>Source</div>
                    <span className={sourceChip(selected.source)}>{selected.source}</span>
                  </div>
                  <div>
                    <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', marginBottom: 3 }}>User</div>
                    <div style={{ color: 'var(--text-primary)' }}>{selected.user?.email || selected.slackUserId || 'Unknown'}</div>
                  </div>
                  <div>
                    <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', marginBottom: 3 }}>Linked records</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {selectedLinks.length ? selectedLinks.map((entity) => {
                        const link = recordLink(entity);
                        const label = recordLabel(entity);
                        return link ? (
                          <Link key={`${entity.entityType}:${entity.entityId}`} to={link} className="chip gray" style={{ textDecoration: 'none' }}>
                            {label}
                          </Link>
                        ) : (
                          <span key={`${entity.entityType}:${entity.entityId}`} className="chip gray">
                            {label}
                          </span>
                        );
                      }) : (
                        <span className="muted">None</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', marginBottom: 3 }}>Updated</div>
                    <div className="mono" style={{ color: 'var(--text-primary)' }}>{formatTime(selected.lastMessageAt || selected.updatedAt)}</div>
                  </div>
                </div>
              )}

              <AssistantTranscriptList
                emptyLabel="No messages stored for this conversation."
                gap={10}
                gridTemplateColumns="90px minmax(0, 1fr) minmax(148px, 170px)"
                messages={messages}
                renderDetails={(message) => <ContractLookupDebug debug={contractLookupDebug(message)} />}
                renderMeta={(message) => (
                  <>
                    <div style={{ whiteSpace: 'nowrap' }}>{formatTime(message.createdAt)}</div>
                    {message.action && (
                      <div
                        title={message.action}
                        style={{
                          marginTop: 4,
                          maxWidth: '100%',
                          marginLeft: 'auto',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {message.action}
                      </div>
                    )}
                  </>
                )}
                rowStyle={{ padding: '10px 12px', fontSize: 12.5 }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
